import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FcGoogle } from 'react-icons/fc';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const YANDEX_URL = 'https://yandex.com.tr/maps/org/inga_burger/65187244632/';

const card = {
  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
  padding: 20, boxShadow: '0 1px 3px rgba(15,23,42,0.04)'
};

function formatDate(value) {
  if (!value) return 'Tarih belirtilmemiş';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Tarih belirtilmemiş' : date.toLocaleDateString('tr-TR');
}

function Stars({ value }) {
  const count = Math.max(0, Math.min(5, Math.round(Number(value) || 0)));
  return <span style={{ color: '#d97706', letterSpacing: 2 }} aria-label={`${count} yıldız`}>
    {'★'.repeat(count)}{'☆'.repeat(5 - count)}
  </span>;
}

export default function MusteriYorumlariPage() {
  const [google, setGoogle] = useState(null);
  const [yandex, setYandex] = useState(null);
  const [yandexError, setYandexError] = useState('');
  const [loadingYandex, setLoadingYandex] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [ratingFilter, setRatingFilter] = useState('all');
  const [search, setSearch] = useState('');

  const loadGoogle = useCallback(async () => {
    setLoadingGoogle(true);
    setGoogleError('');
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/raporlar/google-yorumlari`, {
        headers: { Authorization: `Bearer ${token || ''}` }
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = payload.detail;
        const message = typeof detail === 'string'
          ? detail
          : detail?.google_error?.error?.message || detail?.message || `HTTP ${response.status}`;
        throw new Error(message);
      }
      setGoogle(payload);
    } catch (error) {
      setGoogleError(error.message || 'Google yorumları yüklenemedi.');
    } finally {
      setLoadingGoogle(false);
    }
  }, []);

  const loadYandex = useCallback(async () => {
    setLoadingYandex(true);
    setYandexError('');
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/raporlar/yandex-yorumlari`, {
        headers: { Authorization: `Bearer ${token || ''}` }
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = payload.detail;
        throw new Error(typeof detail === 'string' ? detail : detail?.message || `HTTP ${response.status}`);
      }
      setYandex(payload);
    } catch (error) {
      setYandexError(error.message || 'Yandex yorumları yüklenemedi.');
    } finally {
      setLoadingYandex(false);
    }
  }, []);

  useEffect(() => { loadGoogle(); loadYandex(); }, [loadGoogle, loadYandex]);

  const reviews = useMemo(() => {
    const all = Array.isArray(google?.reviews) ? google.reviews : [];
    const q = search.toLocaleLowerCase('tr-TR').trim();
    return all.filter((item) => (
      (ratingFilter === 'all' || Number(item.rating) === Number(ratingFilter)) &&
      `${item.author || ''} ${item.text || ''}`.toLocaleLowerCase('tr-TR').includes(q)
    )).sort((a, b) => new Date(b.publish_time || 0) - new Date(a.publish_time || 0));
  }, [google, ratingFilter, search]);

  const yandexReviews = useMemo(() => {
    const all = Array.isArray(yandex?.reviews) ? yandex.reviews : [];
    const q = search.toLocaleLowerCase('tr-TR').trim();
    return all.filter((item) => (
      (ratingFilter === 'all' || Number(item.rating) === Number(ratingFilter)) &&
      `${item.author || ''} ${item.text || ''}`.toLocaleLowerCase('tr-TR').includes(q)
    )).sort((a, b) => new Date(b.publish_time || 0) - new Date(a.publish_time || 0));
  }, [yandex, ratingFilter, search]);

  return (
    <div style={{ padding: 24, background: '#f8fafc', minHeight: '100%', color: '#0f172a' }}>
      <h2 style={{ margin: '0 0 4px' }}>Müşteri Yorumları</h2>
      <p style={{ margin: '0 0 20px', color: '#64748b' }}>İNGA Burger — Google ve Yandex değerlendirmeleri</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}><FcGoogle size={24} /> Google</div>
          <div style={{ fontSize: 30, fontWeight: 700, marginTop: 10 }}>{google?.rating != null ? `${google.rating} / 5` : '— / 5'}</div>
          <div style={{ color: '#64748b' }}>Toplam değerlendirme: {google?.total_reviews ?? '—'}</div>
          {googleError && <p style={{ color: '#b91c1c', fontSize: 13, marginBottom: 0 }}>Google verileri şu anda alınamıyor.</p>}
        </div>
        <div style={card}>
          <div style={{ fontWeight: 700 }}>📍 Yandex</div>
          <div style={{ fontSize: 30, fontWeight: 700, marginTop: 10 }}>{yandex?.rating != null ? `${yandex.rating} / 5` : '— / 5'}</div>
          <div style={{ color: '#64748b' }}>Toplam değerlendirme: {yandex?.total_reviews ?? '—'}</div>
          <div style={{ color: '#64748b', fontSize: 13 }}>Yazılı yorum: {yandex?.written_reviews ?? '—'} · Alınabilen: {yandex?.fetched_reviews ?? '—'}</div>
          {yandexError && <p style={{ color: '#b91c1c', fontSize: 13, marginBottom: 0 }}>Yandex verileri şu anda alınamıyor.</p>}
        </div>
      </div>

      <section style={{ ...card, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><FcGoogle size={23} /> Google Yorumları</h3>
          <button type="button" onClick={loadGoogle} disabled={loadingGoogle} style={{ padding: '9px 14px', borderRadius: 8, border: 0, background: '#2563eb', color: '#fff', cursor: 'pointer' }}>
            {loadingGoogle ? 'Yükleniyor...' : 'Google Yorumlarını Yenile'}
          </button>
        </div>
        {googleError && <p role="alert" style={{ color: '#b91c1c', padding: 12, background: '#fef2f2', borderRadius: 8 }}>{googleError}</p>}
        <p style={{ fontSize: 13, color: '#64748b' }}>Google Places API, tüm geçmiş yorumları değil, sınırlı sayıda yorumu döndürür.</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <input aria-label="Yorum ara" placeholder="Yorum veya müşteri ara" value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: 180, padding: 10, border: '1px solid #cbd5e1', borderRadius: 8 }} />
          <select aria-label="Yıldız filtresi" value={ratingFilter} onChange={e => setRatingFilter(e.target.value)} style={{ padding: 10, border: '1px solid #cbd5e1', borderRadius: 8 }}>
            <option value="all">Tüm yıldızlar</option>
            {[5,4,3,2,1].map(n => <option value={n} key={n}>{n} yıldız</option>)}
          </select>
        </div>
        {reviews.length === 0 && !loadingGoogle && <p style={{ color: '#64748b' }}>Gösterilecek Google yorumu bulunmuyor.</p>}
        {reviews.map((review, index) => (
          <article key={`${review.publish_time || ''}-${index}`} style={{ borderTop: '1px solid #e2e8f0', padding: '15px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}><strong>{review.author || 'Anonim'}</strong><span style={{ color: '#64748b' }}>{formatDate(review.publish_time)}</span></div>
            <div style={{ margin: '8px 0' }}><Stars value={review.rating} /></div>
            <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{review.text || 'Yorum metni yok.'}</p>
            {review.google_maps_url && <a href={review.google_maps_url} target="_blank" rel="noopener noreferrer">Google Haritalar'da görüntüle</a>}
          </article>
        ))}
      </section>

      <section style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>📍 Yandex Yorumları</h3>
          <button type="button" onClick={loadYandex} disabled={loadingYandex} style={{ padding: '9px 14px', borderRadius: 8, border: 0, background: '#dc2626', color: '#fff', cursor: 'pointer' }}>
            {loadingYandex ? 'Yükleniyor...' : 'Yandex Yorumlarını Yenile'}
          </button>
        </div>
        {yandexError && <p role="alert" style={{ color: '#b91c1c', padding: 12, background: '#fef2f2', borderRadius: 8 }}>{yandexError}</p>}
        <p style={{ fontSize: 13, color: '#64748b' }}>Yandex sayfasının HTML içeriğinden erişilebilen yorumlar gösterilir. Arama ve yıldız filtresi iki bölüm için ortaktır.</p>
        {!loadingYandex && !yandexError && yandexReviews.length === 0 && <p style={{ color: '#64748b' }}>Gösterilecek Yandex yorumu bulunmuyor.</p>}
        {yandexReviews.map((review, index) => (
          <article key={`${review.publish_time || ''}-${index}`} style={{ borderTop: '1px solid #e2e8f0', padding: '15px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <strong>{review.author || 'Anonim'}</strong>
              <span style={{ color: '#64748b' }}>{formatDate(review.publish_time)}</span>
            </div>
            <div style={{ margin: '8px 0' }}><Stars value={review.rating} /></div>
            <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{review.text || 'Yorum metni yok.'}</p>
          </article>
        ))}
        <a href={YANDEX_URL} target="_blank" rel="noopener noreferrer">İNGA Burger yorumlarını Yandex Haritalar'da aç ↗</a>
      </section>
    </div>
  );
}
